/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import isObjectEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import values from 'lodash.values';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {
  getApplications,
  getArchitecturesAndCategories,
  getDeviceConfigurations,
  getManufacturingAuxValues,
  getPegBehaviors,
  getPlatformDeviceTypes,
  getVehicleInfoEnumTableByName,
  loadDeviceTypes,
  loadGroups,
  loadUsers
} from '@ion/core/src/services/Connect';
import {
  entityId,
  MANUFACTURING_AUX_VALUES
} from '@ion/core/src/services/ConnectUtils';
import {
  GenericObject,
  PlatformDeviceTypes
} from '@ion/core/src/types/entities';
import {
  AccountSearchableProperty,
  SortDirection,
  UserSearchableProperty
} from '@ion/core/src/types/services';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {convertPredefinedDateRange, dateFormatter} from '@ion/core/src/utils/DateUtil';
import {filterDeviceFileTypesByPermissions} from '@ion/core/src/utils/DeviceFileUtils';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {getQueryClient} from '@ion/query/src/config/client';
import {createKey, QueryKey} from '@ion/query/src/config/QueryKeys';
import {getStaleTime} from '@ion/query/src/utils/helpers';
import {
  AttributeOperators,
  AttributeTypes,
  DateQuery,
  DefinedOptions,
  DynamicListData,
  GetSelectedQueryType,
  LookUpSource,
  Mapping,
  PayloadTransformType,
  QueryChipData,
  QuerySelection,
  SAVED_QUERIES_VALUE,
  SearchQueryValue,
  SearchScopeMatch,
  SuggestionsType
} from './types';
import {
  getDateSelection,
  isDateQuery,
  isDateQueryArray,
  isQueryCondition,
  isQueryStringArray,
  isSearchQueryValue,
  multiValueParamsFormatter,
  NO_ID_REPORT,
  transformDeviceTypesToTree
} from './utils';

export const VEHICLE_MAKE_TABLE  = 'makes';
export const VEHICLE_MODEL_TABLE = 'models';

const VEHICLE_ENUM_STALE_TIME = getStaleTime(30, false, false) as number;

/**
 * Defines Base class for Modular Search
 *
 * ```typescript
 * import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
 *
 * const searchInstance = new BaseSearchMappings(mappings);
 * ```
 */
export default class BaseSearchMapping {
  private _mappings: {[key: string]: Mapping};
  private _conditionalRegex = null;
  private _accountId: Number = null;
  private _queryClient;
  private _dynamicList: {[key: string]: DynamicListData} = {};
  private _viewSubGroups: boolean;
  private _loaders = {
    [LookUpSource.Group]                      : this._getGroups,
    [LookUpSource.DeviceType]                 : this._getDeviceTypes,
    [LookUpSource.PegBehavior]                : this._getPegBehaviors,
    [LookUpSource.Application]                : this._getApplications,
    [LookUpSource.Role]                       : this._getRoles,
    [LookUpSource.DeviceFiles]                : this._getDeviceFiles,
    [LookUpSource.Categories]                 : this._getCategories,
    [LookUpSource.Architectures]              : this._getArchitectures,
    [LookUpSource.ManufacturingConfiguration] : this._getDeviceConfigurations,
    [LookUpSource.ManufacturingCarrier]       : this._getCarriers,
    [LookUpSource.VehicleMake]                : this._getVehicleEnum(VEHICLE_MAKE_TABLE, 'Make1'),
    [LookUpSource.VehicleModel]               : this._getVehicleEnum(VEHICLE_MODEL_TABLE, 'Model1')
  };

  constructor($mappings: {[key: string]: Mapping}, $uniqueCondition: string, account: any) {
    this._viewSubGroups = account?.viewSubGroups || false;
    this._mappings = $mappings;
    this._conditionalRegex = new RegExp(`\\s${$uniqueCondition}`);
    this._accountId = account?.accountId || account;
    this._queryClient = getQueryClient();
    this._getInitialDynamicDataFromMapping(this);
  }

  /**
   * Function to initialize data loaders for given mapping.
   * @private
   */
  private _getInitialDynamicDataFromMapping($this) {
    if (isObjectEmpty($this._dynamicList)) {
      const dynamicAttributes = values($this._mappings).map((mapping: Mapping) => {
        if (mapping.suggestion === SuggestionsType.LOOKUP) {
          return mapping.suggestionsSource;
        }
        return null;
      }).filter(source => source !== null);
      this._initializeDynamicData(dynamicAttributes, $this);
    }
  }

  /**
   * Function to get loaders for the dynamic data based on mapping.
   * @private
   */
  private _initializeDynamicData(items: LookUpSource[], $this) {
    items.forEach((item: string) => {
      $this._dynamicList[item] = {
        loader : $this._loaders[item]($this)
      };
    });
  }

  get mappings() {
    return this._mappings;
  }

  static isMultiSelectionType(source) {
    return source === SuggestionsType.MULTISELECTION;
  }

  static isSelectionType(source) {
    return (
      source === SuggestionsType.LOOKUP ||
      source === SuggestionsType.SELECTION
    );
  }

  static isDateSelection(source) {
    return source === SuggestionsType.DATE;
  }

  /**
   * Function return object to be passed to request for search results
   * @param queries array of QueryChipData data
   * @return object
   */
  transformQueriesToRequestParams(queries: QueryChipData[], isExactMatch?: SearchScopeMatch): {[key: string]: any} {
    const requestParams = {};
    if (queries?.length) {
      queries.forEach(({attribute, value, label}: QueryChipData) => {
        const {
          attributes,
          hasNoCharacterLimit,
          hasPartialSubString,
          hasSpecialCharacter,
          useLookupOptionName,
          hasLargeMaxLimit,
          hasAnyField,
          hasCollection,
          hasCommaSeparated,
          hasIDMaxLimit,
          hasExactMatch
        } = this._mappings[attribute] || {};

        requestParams[attribute] = this._constructRequestString(
          attributes?.value,
          value,
          {
            hasNoCharacterLimit,
            hasPartialSubString,
            hasSpecialCharacter,
            useLookupOptionName,
            hasLargeMaxLimit,
            hasAnyField,
            hasCollection,
            hasCommaSeparated,
            hasIDMaxLimit,
            hasExactMatch,
            isExactMatch,
            attribute
          }
        );
      });
    }
    return requestParams;
  }

  /**
   * Function to split query into chips. Returns array of strings or null
   * @param searchQuery query string formed
   * @returns array of strings or null if string is empty
   */
  createQueryChips(queryChip): QueryChipData|null {
    if (!isNil(queryChip) && !isNil(queryChip.attribute) && !isNil(queryChip.queryOption.query)) {
      const {attribute, queryOption, label: queryChipLabel} = queryChip;
      const {options, suggestion, label, hasNoCharacterLimit} = this._mappings[attribute];
      const hasAnyField = label?.includes('Any Field');
      if (queryOption) {
        const result = {
          label     : '',
          attribute : attribute,
          value     : null
        };
        if (suggestion === SuggestionsType.TEXT) {
          const {query} = queryOption;
          const format = /[, \t]+/g;
          const formattedQuery = hasNoCharacterLimit
            ? query.replace(/  +/g, ' ').replace(format, ', ')
            : query;

          result.label = `${label}: ${formattedQuery}`;
          result.value = hasNoCharacterLimit ? formattedQuery.split(',') : [query];
        }
        if (options && suggestion !== SuggestionsType.TEXT) {
          const {value, label: valueLabel} = this._getChipValues(attribute, queryOption);
          result.label = `${label}: ${valueLabel}`;
          result.value = value;
        }
        if (suggestion === SuggestionsType.LOOKUP) {
          result.label = `${label}: ${queryOption.query}`;
          result.value = queryOption.option;
        }
        if (suggestion === SuggestionsType.DATE) {
          const queryLabel = queryOption.option?.label;
          if (!isString(queryLabel) && queryLabel) {
            const dateValues = getDateSelection(queryLabel);
            result.value = dateValues;
            result.label = `${label}: ${dateValues.map(d => dateFormatter(d, 'MM/DD/YYYY')).join(' - ')}`;
          } else if (queryLabel) {
            result.label = `${label}: ${queryLabel}`;
            result.value = {...queryOption.option};
            if (queryLabel === NO_ID_REPORT) {
              result.label = `${label}: ${queryLabel}`;
              result.value = queryLabel;
            }
          } else {
            result.label = `${label}: ${queryOption.query || ''}`;
            result.value = queryOption.option || queryOption.query;
          }
        }
        if (hasNoCharacterLimit && hasAnyField && (result?.label?.match(/(AND|OR)/gi)?.length > 0)) {
          result.label = result?.label?.replaceAll(',', ' ');
        }
        return result;
      }
      return null;
    }
    return null;
  }

  private _getChipValues(attribute, queryOption): {value: GenericObject, label: string} {
    const chipValues = {
      value : queryOption?.option,
      label : queryOption?.query
    };

    const {suggestion, options} = this._mappings[attribute];
    if (suggestion === SuggestionsType.MULTISELECTION) {
      const queryOptionsList = queryOption?.query?.split(',').map((text: string) => text.trim());
      const mappingSelection = options.filter(({label, value}) => queryOptionsList.includes(label));
      chipValues.label = mappingSelection.reduce((currentValue, nextItem, index) =>
        `${currentValue}${nextItem.value}${index < mappingSelection.length - 1 ? ', ' : ''}`, '');
      chipValues.value = mappingSelection;
    }
    if (suggestion === SuggestionsType.SELECTION) {
      const option = options?.filter(({label}) => label === queryOption?.query);
      if (!isEmpty(option)) {
        const {label, value} = option[0];
        chipValues.label = label;
        chipValues.value = value;
      }
    }
    return chipValues;
  }

  /**
   * Function to check if the current query needs any suggestions dropdown
   * @param query current query entered in the text field
   */
  checkForSuggestionsNeeded(query: string) {
    const suggestions = {
      isSuggestionsNeeded : false,
      suggestionsType     : SuggestionsType.TEXT,
      suggestionsSource   : null
    };
    const [key, condition, value] = this._splitQuery(query);
    if (!condition) {
      suggestions.isSuggestionsNeeded = true;
      suggestions.suggestionsType = SuggestionsType.ATTRIBUTE;
    } else if (key && condition && isEmpty(value)) {
      const mapping = this._getMappingForAttribute(key);
      if (mapping && mapping.suggestion !== SuggestionsType.TEXT) {
        suggestions.isSuggestionsNeeded = true;
      }
      suggestions.suggestionsType = mapping.suggestion;
      suggestions.suggestionsSource = mapping.suggestionsSource;
    }
    return suggestions;
  }

  /**
   * Function to return options available for the mapping to be searched in suggestions
   * @param queries previously created queries displayed as chips
   * @param type SuggestionsType
   * @param query current query entered in the text field
   */
  getSearchableOptions(query: string, type: SuggestionsType, queries: QueryChipData[]) {
    if (type === SuggestionsType.ATTRIBUTE) {
      const attibutes = Object.keys(this._mappings);
      return attibutes;
    }
    return type !== SuggestionsType.TEXT ? this._getOptions(query, type) : [];
  }

  /**
   * Function to form query string
   * @param menuItem string selected from dropdown suggestions
   * @param source SuggestionsType
   * @param query current query entered in the text field
   */
  getSelectedQuery({
    menuItem,
    source,
    query,
    attribute
  }: GetSelectedQueryType): string {
    return this._formQuery({
      attribute,
      source,
      query,
      option : menuItem
    });
  }

  /**
   * Function to return the currently selected options for the query being created
   * @param query current query string entered
   */
  getSelectedSuggestion(query: string): string[] {
    if (query?.length) {
      return typeof query === 'string' ? query.split(', ') : query;
    }
    return [];
  };

  getLabel(attribute: string) {
    return attribute !== SAVED_QUERIES_VALUE
      ? this._mappings[attribute]?.label
      : translate('common:modularsearch.savedSearch');
  }

  getCurrentLookup(source: LookUpSource) {
    return this._dynamicList[source];
  }

  /**
   * Resolves the option label a chip's stored value was selected from.
   *
   * The inverse of `_getChipValues` for a single selection: a chip holds the
   * option's value, which is what the search filters on, while the dropdown and
   * the chip's own label show the option's label. Reopening a chip for editing
   * therefore has to translate the value back, otherwise the search field would
   * show a raw value the user never typed and which matches no option.
   *
   * ```typescript
   *
   *   import BaseSearchMapping from '@ion/features/src/search/BaseSearchMappings';
   *   ...
   *
   *   // 'PROFILE_FOUND' selected as 'Profile Found' ...
   *   const label = searchInstance.getOptionLabel('status', 'PROFILE_FOUND');
   *
   * ```
   *
   * @param   attribute The attribute the chip was created for.
   * @param   value     The value the chip holds.
   * @returns           The matching option's label, or `value` unchanged when the
   *                    mapping offers no single selection option for it.
   */
  getOptionLabel(attribute: string, value: any) {
    const {options, suggestion} = this._mappings[attribute] ?? {};
    let label = value;
    if (suggestion === SuggestionsType.SELECTION) {
      const selected = options?.find((option: DefinedOptions) => option.value === value);
      if (!isNil(selected)) {
        label = selected.label;
      }
    }
    return label;
  }

  getValue(query: string): string[] {
    return !isUndefined(query)
      ? typeof query === 'string'
        ? this._splitQuery(query)
        : query : [];
  }

  getNamesInQueries(queries: QueryChipData[]): string {
    const name = queries.reduce((name, {label}: QueryChipData, index: number) => {
      const [attribute] = this._splitQuery(label);
      return `${name}${attribute}${index < queries.length - 1 ? ', ' : ''}`;
    }, '');
    return name;
  }

  getFirstAttributeLabel(queries?: QueryChipData[], autoSelectNext: boolean = false) {
    const mappingKeys = Object.keys(this._mappings);
    if (queries) {
      const queriesAttributes = this._getAttributesFromSelectedQueries(queries);
      const filteredMapKeys   = mappingKeys.filter((key: string) => !queriesAttributes.includes(key));
      return autoSelectNext ? mappingKeys[0] : filteredMapKeys[0];
    }
    return mappingKeys[0];
  }

  getMappingObject(attribute: string) {
    return this._mappings[attribute];
  }

  /**
   * Function that return chips with annotated values.
   * @public
   */
  decorateChips(chips: QueryChipData[]): QueryChipData[] {
    return chips.map((chip: QueryChipData) => {
      const {attributes, label} = this._mappings[chip.attribute];
      const value = `${label}: ${this._transformValueForChip(chip.value)}`;
      return {...chip, label: value};
    });
  };

  /**
   * Function to check if mapping has fixed character limit
   * @public
   */
  getSelectionCharacterLength(attribute: string) {
    const searchLimit = this._mappings?.[attribute]?.hasLargeMaxLimit ? 650000 : 20000;
    return (this._mappings?.[attribute]?.hasNoCharacterLimit) ||
            (this._mappings?.[attribute]?.hasIDMaxLimit) ? searchLimit : 32;
  }

  /**
   * Function to get the mapping suggestions value like id or name
   * @public
   */
  getSuggestionsField() {
    if (this._mappings) {
      const mappingValues    = Object.values(this._mappings);
      const filteredMapValue = mappingValues?.find(({type}) => type === AttributeTypes.LOOKUP) || {};
      return filteredMapValue?.suggestionsField;
    }
  }

  /**
   * Function to check if mapping is dynamic lookup of type tree view
   */
  isCurrentSelectionTreeView(attribute: string) {
    return this._mappings?.[attribute]?.isTreeView;
  }

  /**
   * Function to choose display label name
   */
  getDisplayableQueryAttribute(queryAttribute: string, {
    isTreeView,
    children: childTypes
  }: {isTreeView: boolean, children: any[]}) {
    if (isTreeView) {
      const {localeValues: {children, root}} = this._mappings[queryAttribute];
      return childTypes ? root : children;
    }
    return queryAttribute;
  }

  /**
   * Function to return [key, condition, value] array
   * @private
   * @param query string
   */
  private _splitQuery(query: string): string[] {
    return query?.trim()?.split(this._conditionalRegex)?.map(token => token.trim());
  }

  /**
   * Function to return the mapping based on label
   * @private
   * @param label string
   */
  private _getMappingForAttribute(label: string): Mapping | null {
    let result = null;
    values(this._mappings).forEach((mapping: Mapping) => {
      if (mapping.label === label) {
        result = mapping;
      }
    });
    return result;
  }

  /**
     * Function to provide a unique list of search attributes
     * @private
     */
  private _getSelectableSearchAttributes(selectedIdentifiers: string[]): string[] {
    return Object.keys(this._mappings)
      .filter((indetifier: string) => !selectedIdentifiers.includes(indetifier));
  };

  /**
   * Retrive attributes for the queries formed in the chips
   * @private
   * @param queries Array of queries created
   */
  private _getAttributesFromSelectedQueries(queries: QueryChipData[]): string[] {
    if (queries.length > 0) {
      return queries.map(({attribute}: QueryChipData) => attribute);
    }
    return [];
  }

  /**
   * Function returns options for the selected attribute if the mapping has predefined options
   * @private
   * @param query string
   * @param type SuggestionsType
   */
  private _getOptions(query: string, type: SuggestionsType) {
    if (type !== SuggestionsType.LOOKUP && type !== SuggestionsType.TEXT) {
      const mappingForAttributes: Mapping = this._mappings[query];
      return mappingForAttributes?.options?.map((option: DefinedOptions) => option.label) || [];
    }
    return [];
  }

  /**
   * Function return string by taking the current search query and add additional parameter based on dropdown selection
   * @private
   * @param query specifies the current input value
   * @param option if source is 'ATTRIBUTE' contains a string that is selected from dropdown option e.g: 'NAME CONTAINS'
   *               or dynamic value if source is 'VALUE'.
   * @param source is an enum to specify where the value is selected from.
   */
  private _formQuery({attribute, option, source, query}: QuerySelection): string {
    if (source === SuggestionsType.MULTISELECTION) {
      const currentValue = this._extractValuesFromMapping(query, this._mappings[attribute].options);
      if (typeof option === 'string') {
        return (currentValue.length) ? (currentValue.length === 46) ? '' : this._getUniqueValues(currentValue, option)
          : option.toString();
      } else if (typeof option === 'object') {
        return (currentValue.length) ? '' : option.toString();
      }
    } else if (source === SuggestionsType.LOOKUP && isSearchQueryValue(option)) {
      return option?.isTreeView ? option?.fullName : option?.name;
    } else if (source === SuggestionsType.DATE && isDateQuery(option)) {
      if (typeof option.label === 'string') {
        return option.value.join(' - ');
      }
      return getDateSelection(option.label).join(' - ');
    }
    return option.toString();
  };

  private _extractValuesFromMapping(queryString, options): string {
    const optionsValues: string[] = options?.map(({label, value}) => label);
    const value = queryString.split(',').filter((query: string) => optionsValues.includes(query.trim()));
    return value.join(',');
  }

  /**
   * Function that creates API query value based on selected value and AttributeOperators
   * @private
   */
  private _constructRequestString(
    condition: AttributeOperators,
    queryValue: string | DateQuery | SearchQueryValue,
    {
      hasNoCharacterLimit,
      hasPartialSubString,
      hasSpecialCharacter,
      hasLargeMaxLimit,
      hasAnyField,
      hasCollection,
      hasCommaSeparated,
      hasIDMaxLimit,
      hasExactMatch,
      isExactMatch,
      useLookupOptionName,
      attribute
    }
  ): string | number {
    let requestParamValue: string | number = '';
    switch (condition) {
      case AttributeOperators.IN:
        requestParamValue = this._requestValueForSearch(queryValue, null);
        break;
      case AttributeOperators.IS:
        requestParamValue = this._requestValueForSearch(queryValue, AttributeOperators.IS, useLookupOptionName);
        break;
      case AttributeOperators.BETWEEN:
        requestParamValue = this._requestValueForSearch(queryValue, null);
        break;
      default: {
        const currentParamsValue = this._requestValueForSearch(queryValue, condition).toString();
        requestParamValue        = multiValueParamsFormatter({
          queryValue : currentParamsValue,
          hasNoCharacterLimit,
          hasPartialSubString,
          hasSpecialCharacter,
          hasLargeMaxLimit,
          hasAnyField,
          hasCollection,
          hasCommaSeparated,
          hasIDMaxLimit,
          hasExactMatch,
          isExactMatch,
          attribute
        });
        break;
      }
    }
    return requestParamValue;
  }

  /**
   * Function to get unique values from object
   * @private
   */
  private _getUniqueValues(currentValues: string, item: string | Mapping) {
    const currentValuesArray = currentValues.trim().split(', ');
    if (typeof item === 'string' && currentValuesArray.includes(item)) {
      const data = currentValuesArray.filter((value: string) => value !== item).join(', ');
      return data;
    }
    return `${currentValues}, ${item}`;
  }

  /**
   * Function to provide value decorator in Query Chip based on selected dropdown value type.
   * @private
   */
  private _transformValueForChip(
    labelObject: string | SearchQueryValue | DateQuery | DateQuery[]
  ) {
    let val = null;
    if (typeof labelObject !== 'string' && isSearchQueryValue(labelObject)) val = labelObject.name.trim();
    if (typeof labelObject !== 'string' && isDateQuery(labelObject)) val = labelObject.label.trim();
    if (typeof labelObject !== 'string' && isDateQueryArray(labelObject)) {
      val = labelObject
        .reduce(function(currentVal, {label}, index) {
          currentVal += `${label}${index < labelObject?.length - 1 ? ', ' : ''}`;
          return currentVal;
        }, '');
    }
    if (typeof labelObject === 'string') val = labelObject.trim();
    return val;
  }

  /**
   * Function that will fetch API data for all available pages
   * @private
   */
  private _getDynamicListData(
    loader: Function,
    responseIdentifier: string,
    payload: {[key: string]: any}
  ) {
    return (async function() {
      let currentPage = 1;
      let totalPages = 1;
      let dataSet = [];
      while (currentPage <= totalPages) {
        const loaderPayload = {
          ...payload,
          pageId   : currentPage,
          pageSize : 100
        };
        const currentDataSet = await loader({...loaderPayload});
        dataSet = [...dataSet, ...currentDataSet[responseIdentifier]];
        totalPages = Math.ceil(currentDataSet.totalCount/100);
        currentPage += 1;
      };
      return dataSet;
    })();
  }

  /**
   * Function to dispatch getGroups API for available data across multiple pages.
   * @private
   */
  private _getGroups($this) {
    return async function(name: string) {
      const groups = await $this._getDynamicListData(
        loadGroups,
        'groups',
        {
          accountId        : $this._accountId,
          sortOn           : [`${SortDirection.Ascending}${AccountSearchableProperty.name}`],
          includeSubGroups : $this?._viewSubGroups || false,
          name             : !isNil(name) ? name : null
        }
      );
      const groupList = $this._transformToDynamicDataItemList(groups);
      groupList.data.unshift({
        name : translate('common:modularsearch.noGroup'),
        id   : -1
      });
      return groupList;
    };
  }

  /**
   * Function to dispatch getDeviceTypes API for available data across multiple pages.
   * @private
   */
  private _getDeviceTypes($this) {
    const suggestionsField = $this.getSuggestionsField();
    return async function() {
      const deviceTypes = await $this._getDynamicListData(
        loadDeviceTypes,
        'items',
        {
          sortOn : [`${SortDirection.Ascending}${AccountSearchableProperty.name}`]
        }
      );
      const data = $this._transformToDynamicDataItemList(deviceTypes, suggestionsField);
      return data;
    };
  }

  /**
   * Function to dispatch getPegBehaviors API for available data across multiple pages.
   * @private
   */
  private _getPegBehaviors($this) {
    return async function() {
      const pegBehaviors = await $this._getDynamicListData(
        getPegBehaviors,
        'pegBehaviors',
        {
          accountId : $this._accountId,
          sortOn    : [`${SortDirection.Ascending}${AccountSearchableProperty.name}`]
        }
      );
      return $this._transformToDynamicDataItemList(pegBehaviors);
    };
  }

  /**
   * Function to dispatch getApplications API for available data.
   * @private
   */
  private _getApplications($this) {
    return async function() {
      const applications = await getApplications($this._accountId);
      const result = $this._transformToDynamicDataItemList(applications, 'applicationKey');
      sortByString(result.data, 'name');
      return result;
    };
  }

  private _getCarriers($this) {
    return async function() {
      try {
        const carriers = await getManufacturingAuxValues();
        const result = $this._transformToDynamicDataItemList(carriers[MANUFACTURING_AUX_VALUES.CARRIER]);
        sortByString(result.data, 'name');
        return result;
      } catch (err) {
        error('An Error occurred while retrieving Manufacturing Carrier Auxiliary Values', err?.toString);
        return [];
      }
    };
  }

  private _getDeviceFiles($this) {
    return async function() {
      let filteredDeviceFiles: PlatformDeviceTypes = [];
      const mapping     = $this?._mappings?.platformId || {};
      const deviceFiles = await getPlatformDeviceTypes() as unknown as PlatformDeviceTypes;
      const {
        hasFeatures,
        hasFilePermission
      } = mapping || {};
      if (deviceFiles?.length) {
        filteredDeviceFiles = filterDeviceFileTypesByPermissions(hasFeatures, hasFilePermission, deviceFiles);
      }
      return {
        data         : transformDeviceTypesToTree(filteredDeviceFiles),
        isDeviceFile : true
      };
    };
  }

  private _getRoles($this) {
    return async function(name: string) {
      const users = await $this._getDynamicListData(
        loadUsers,
        'users',
        {
          accountId        : $this._accountId,
          sortOn           : [`${SortDirection.Ascending}${UserSearchableProperty.username}`],
          includeSubGroups : $this?._viewSubGroups || false,
          name             : !isNil(name) ? name : null
        }
      );
      const userrList = $this._transformToDynamicDataItemList(users);
      userrList.data.unshift({
        name : translate('common:modularsearch.noRole'),
        id   : -1
      });
      return userrList;
    };
  }

  /**
   * Function to load one Vehicle Info enum table, cached under its own query key.
   * @private
   */
  private _getVehicleEnum(table: string, valueProperty: string) {
    return ($this) => async function() {
      try {
        const rows = await $this._queryClient.fetchQuery({
          queryKey : createKey(QueryKey.VEHICLE_PROFILE_ENUM, table),
          queryFn  : async function() {
            return await getVehicleInfoEnumTableByName(table);
          },
          staleTime : VEHICLE_ENUM_STALE_TIME,
          retry     : false
        });
        const entries = rows
          .map((row) => ({name: row[valueProperty], id: row.id}))
          .filter(({name}) => !isEmpty(name));
        const result = $this._transformToDynamicDataItemList(entries);
        sortByString(result.data, 'name');
        return result;
      } catch (err) {
        error(`An Error occurred while retrieving the ${table} vehicle enum table`, err?.toString());
        return {data: [], hasError: true};
      }
    };
  }

  /**
   * Function to dispatch getCategories API for available data.
   * @private
   */
  private _getCategories($this) {
    return async function() {
      const {categories} = await getArchitecturesAndCategories();
      const allCategories = categories?.map((category) => {
        return {name: category.label, id: category.label};
      });
      const result = $this._transformToDynamicDataItemList(allCategories);
      sortByString(result.data, 'name');
      return result;
    };
  }

  /**
   * Function to dispatch getArchitectures API for available data.
   * @private
   */
  private _getArchitectures($this) {
    return async function() {
      const {architectures} = await getArchitecturesAndCategories();
      const allArchitectures = architectures?.map((architecture) => {
        return {name: architecture.label, id: architecture.label};
      });
      const result = $this._transformToDynamicDataItemList(allArchitectures);
      sortByString(result.data, 'name');
      return result;
    };
  }

  private _getDeviceConfigurations($this) {
    return async function() {
      const deviceConfigurations = await getDeviceConfigurations($this._accountId);
      const result = $this._transformToDynamicDataItemList(
        deviceConfigurations
      );
      sortByString(result.data, 'name');
      return result;
    };
  }

  /**
   * Function to transform API data object to {id, name}[]
   * @private
   */
  private _transformToDynamicDataItemList(payload: any, idProp: string = 'id'): PayloadTransformType {
    const result = {
      data : []
    };
    if (!isObjectEmpty(payload)) {
      result.data = payload.map((item: any) => ({
        name      : item.name,
        id        : item[idProp],
        accountId : entityId(item?.account) || null
      }));
    }
    return result;
  }

  /**
   * @private
   */
  private _requestValueForSearch(
    queryValue: string | DateQuery | SearchQueryValue | DateQuery[],
    condition: AttributeOperators | null,
    useLookupOptionName = false
  ) {
    if (typeof queryValue === 'string') {
      const dateRange = convertPredefinedDateRange(queryValue);
      if (dateRange) {
        return dateRange;
      }
      return isQueryCondition(condition, AttributeOperators.IS)
        ? queryValue.trim()
        : queryValue.split(', ').join(',').trim();
    }
    if (isQueryStringArray(queryValue) && isQueryCondition(
      condition,
      AttributeOperators.IS,
      AttributeOperators.CONTAINS
    )) {
      return queryValue.filter((value) => value?.length > 0).join(', ');
    }
    if (isSearchQueryValue(queryValue)) {
      return useLookupOptionName ? queryValue.name : queryValue.id;
    }
    if (isDateQuery(queryValue)) {
      return `${queryValue.value[0]} - ${queryValue.value[1]}`;
    }
    if (isDateQueryArray(queryValue)) {
      const valuesArray = queryValue.reduce((acc, curr, index) => {
        acc.push(curr.value);
        return acc;
      }, []);
      return valuesArray.join(', ');
    }
    if (queryValue?.length > 0 && new Date(queryValue[0])) {
      const dateRanges = [];
      if (queryValue?.[0]) {
        dateRanges.push(dateFormatter(queryValue[0], 'MM/DD/YYYY'));
      }
      if (queryValue?.[1]) {
        dateRanges.push(dateFormatter(queryValue[1], 'MM/DD/YYYY'));
      }
      return dateRanges.join(' - ');
    }
  }
}
